/**
 * Songs — §04 Significado, picker → single-song deck.
 *
 *   index === null  →  <SongsPicker> (3 cards, "Leer significado →")
 *   index in 0..n-1 →  <SongStage key={index}> (5 beats + back pill)
 *
 * Switching songs is picker-only. No keyboard arrow nav, no in-stage
 * prev/next. The "← Volver al picker" pill on the stage is the only path
 * back. Album swaps reset state because <Songs> is keyed in App.tsx.
 *
 * Picker ↔ stage handoff is wrapped in AnimatePresence (mode="wait"):
 *  - Picker exits with a clean fade (~200ms).
 *  - Stage mounts and lifts in (y: 18 → 0, ~520ms).
 *  - Same variant runs in reverse on "Volver al picker", which also
 *    smooth-scrolls back to the section header so the picker is in view.
 *  - prefers-reduced-motion: variants collapse to 0-duration, scroll is
 *    instant.
 *
 * Beat-level reveals inside <SongStage> are driven by <Reveal>; this
 * component only orchestrates the swap.
 */

import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import {
  prefersReducedMotion,
  useReducedMotion,
} from '@/animation/reducedMotion';
import { useTrack } from '@/analytics';
import { Reveal } from '../primitives/Reveal';
import { SongsPicker } from './SongsPicker';
import { SongStage } from './SongStage';
import type { AvailableAlbumModule } from '@/types/album';

interface SongsProps {
  album: AvailableAlbumModule;
}

/** Soft cubic-out used everywhere on the page for entrances. Kept inline
 *  because the variant only consumes it; no need to leak another export
 *  out of tiers.ts. */
const EASE_OUT: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

function buildSwapVariants(reduced: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduced ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0 : 0.52, ease: EASE_OUT },
    },
    exit: {
      opacity: 0,
      y: reduced ? 0 : -4,
      transition: { duration: reduced ? 0 : 0.2, ease: EASE_OUT },
    },
  };
}

export function Songs({ album }: SongsProps): JSX.Element {
  const songs = album.songs;
  const [index, setIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const reduced = useReducedMotion();
  const swapVariants = buildSwapVariants(reduced);
  const track = useTrack();

  /** Pickers normally sit near the top of the section, so no auto-scroll
   *  on pick — the user is already roughly at Beat 1. */
  const onPick = useCallback(
    (i: number) => {
      if (!Number.isInteger(i) || i < 0 || i >= songs.length) return;
      const song = songs[i];
      if (!song) return;
      track('Song Open', { song: song.title, index: i });
      setIndex(i);
    },
    [songs, track]
  );

  /** Returning from a long stage often lands the user thousands of pixels
   *  below the picker. Defer the scroll one frame so React commits the
   *  picker remount before we move the viewport. Re-queries the OS-level
   *  reduced-motion setting at click time (the hook value can be stale
   *  if the user toggled it between renders). */
  const onBack = useCallback(() => {
    setIndex(null);
    requestAnimationFrame(() => {
      const target = sectionRef.current;
      if (!target) return;
      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }, []);
  const selectedSong = index === null ? null : (songs[index] ?? null);

  return (
    <section
      className="sm-B"
      id="songs"
      aria-labelledby="songs-heading"
      ref={sectionRef}
    >
      <Reveal as="div" className="sm-section-head">
        <div className="sm-section-num">
          <span className="num">04</span>Significado
        </div>
        <h2 className="sm-section-title" id="songs-heading">
          {album.library.songsTitle}
        </h2>
      </Reveal>

      <AnimatePresence mode="wait" initial={false}>
        {!selectedSong ? (
          <motion.div
            key="picker"
            variants={swapVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SongsPicker songs={songs} onPick={onPick} />
          </motion.div>
        ) : (
          <motion.div
            key={`stage-${index}`}
            variants={swapVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SongStage song={selectedSong} onBack={onBack} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
